// The Echo Room — melodic ear-training sits. Pick a rung on the ladder
// (two notes → three → little phrases → the minor side, once it's open),
// then echo five calls back. The capture wiring is a local copy of
// EchoMoment's jump-in pattern — the instrument leads: playing over the
// call's tail (or from review) starts your echo, no button round-trips.
// Feedback speaks compareEcho's warm voice, then the room auto-advances;
// "again" replays the same call, and the end card sums the sit in plain
// words. Nothing gates on success and nothing here is scored.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { compareEcho, phraseLength } from "../../../lib/homeKeyTheory";
import { PATCHES, playPhrase, resumeAudio } from "../../../lib/homeKeyAudio";
import { ECHO_TIERS, echoSitPlan } from "../../../lib/homeKeyPractice";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

// Capture closes this long after your last note-off — patient while your
// echo is still shorter than the call, brisk once you've matched its length.
const WINDOW_SHORT_MS = 1800;
const WINDOW_LONG_MS = 3500;
// How long the feedback hangs before the next call rolls in on its own.
const AUTO_ADVANCE_MS = 3200;

// How one call went, in plain words — the vocabulary of the end card.
const GRADE_WORDS = {
  perfect: "note for note",
  close: "close",
  far: "worth another listen",
  quiet: "let drift by",
};
const GRADE_ORDER = ["perfect", "close", "far", "quiet"];

export default function EchoRoom({ completed, savedTier, onTier, onExit }) {
  const [tier, setTier] = useState(null);
  const [callIndex, setCallIndex] = useState(0);
  const [phase, setPhase] = useState("pick"); // pick | call | capturing | review | end
  const [echo, setEcho] = useState(null);
  const [result, setResult] = useState(null);
  const [noteCount, setNoteCount] = useState(0);
  const [results, setResults] = useState([]); // one grade word key per finished call

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const callIndexRef = useRef(callIndex);
  callIndexRef.current = callIndex;
  const planRef = useRef([]);
  const echoRef = useRef(null);
  const resultRef = useRef(null);

  const recState = useRef({ t0: null, starts: new Map(), notes: [] });
  const windowTimer = useRef(null);
  const advanceTimer = useRef(null);
  const playRef = useRef(null);
  const timersRef = useRef([]);
  const advanceRef = useRef(() => {});

  const stopPlayback = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (windowTimer.current) clearTimeout(windowTimer.current);
    windowTimer.current = null;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
  }, []);
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const setPhaseNow = (next) => {
    // phaseRef must move with the phase synchronously — recorder callbacks
    // fire between renders.
    phaseRef.current = next;
    setPhase(next);
  };

  // ----- capture plumbing (EchoMoment's jump-in wiring, copied local) -----

  const beginCapture = () => {
    stopPlayback(); // if the call is still ringing, you've taken over
    recState.current = { t0: null, starts: new Map(), notes: [] };
    echoRef.current = null;
    resultRef.current = null;
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    setPhaseNow("capturing");
  };

  const finishCapture = () => {
    if (phaseRef.current !== "capturing") return;
    voices.releaseAll(); // closes (and records) anything still held
    if (windowTimer.current) clearTimeout(windowTimer.current);
    windowTimer.current = null;
    const notes = [...recState.current.notes].sort((a, b) => a.at - b.at);
    const phrase = notes.length ? { notes } : null;
    const call = planRef.current[callIndexRef.current];
    const res = phrase ? compareEcho(call, phrase) : null;
    echoRef.current = phrase;
    resultRef.current = res;
    setEcho(phrase);
    setResult(res);
    setPhaseNow("review");
    // The room breathes on its own: feedback hangs a moment, then the next
    // call rolls in. Replaying, retrying, or "again" all cancel this.
    advanceTimer.current = setTimeout(() => advanceRef.current(), AUTO_ADVANCE_MS);
  };

  const recorder = {
    onNoteStart: (midi, tMs) => {
      const ph = phaseRef.current;
      // The instrument leads: playing over the call's tail, or playing again
      // from review, simply starts (re)capturing — this very note included.
      if (ph === "call" || ph === "review") beginCapture();
      else if (ph !== "capturing") return;
      const r = recState.current;
      if (r.t0 == null) r.t0 = tMs;
      r.starts.set(midi, tMs);
      if (windowTimer.current) clearTimeout(windowTimer.current);
      windowTimer.current = null;
    },
    onNoteEnd: (midi, tMs) => {
      if (phaseRef.current !== "capturing") return;
      const r = recState.current;
      const start = r.starts.get(midi);
      if (start == null) return;
      r.starts.delete(midi);
      r.notes.push({
        midi,
        at: Math.round(start - r.t0),
        dur: Math.max(40, Math.round(tMs - start)),
      });
      setNoteCount(r.notes.length);
      if (r.starts.size === 0) {
        const call = planRef.current[callIndexRef.current] || {};
        const callNotes = (call.notes || []).length;
        const wait = r.notes.length >= callNotes ? WINDOW_SHORT_MS : WINDOW_LONG_MS;
        windowTimer.current = setTimeout(finishCapture, wait);
      }
    },
  };

  const voices = useHomeKeyVoices(PATCHES.softKeys, recorder);
  const { press, release, activeMidis } = voices;
  const inputsLive = phase !== "pick" && phase !== "end";
  useTypingKeys(press, release, inputsLive);
  useHomeKeyMidi(press, release, inputsLive);

  // ----- flow -----

  const playCall = (index = callIndexRef.current) => {
    resumeAudio();
    stopPlayback();
    echoRef.current = null;
    resultRef.current = null;
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    setPhaseNow("call");
    const call = planRef.current[index];
    const handle = playPhrase(call, PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    // Open capture right when the call's last note ends — echoing over the
    // tail feels natural (and any earlier press opens it even sooner).
    const openMs = (phraseLength(call) || 2000) + 150;
    timersRef.current.push(
      setTimeout(() => {
        if (phaseRef.current === "call") {
          recState.current = { t0: null, starts: new Map(), notes: [] };
          setPhaseNow("capturing");
        }
      }, openMs)
    );
  };

  const replayCall = () => {
    resumeAudio();
    stopPlayback(); // pauses the auto-advance too — reviewing takes its time
    playRef.current = playPhrase(planRef.current[callIndex], PATCHES.softKeys);
  };
  const replayEcho = () => {
    if (!echo) return;
    resumeAudio();
    stopPlayback();
    playRef.current = playPhrase(echo, PATCHES.softKeys);
  };

  const gradeOf = () => {
    if (!echoRef.current) return "quiet";
    const r =
      resultRef.current ||
      compareEcho(planRef.current[callIndexRef.current], echoRef.current);
    if (r.perfect) return "perfect";
    if (r.matched >= Math.max(1, Math.ceil(r.total * 0.6))) return "close";
    return "far";
  };

  const advance = () => {
    stopPlayback();
    voices.releaseAll();
    const idx = callIndexRef.current;
    setResults((rs) => [...rs, gradeOf()]);
    echoRef.current = null;
    resultRef.current = null;
    setEcho(null);
    setResult(null);
    setNoteCount(0);
    if (idx >= planRef.current.length - 1) {
      setPhaseNow("end");
    } else {
      const next = idx + 1;
      callIndexRef.current = next;
      setCallIndex(next);
      playCall(next);
    }
  };
  advanceRef.current = advance;

  const startSit = (t) => {
    if (onTier) onTier(t.id); // remembers the rung you chose — nothing else
    planRef.current = echoSitPlan(t.id, { completed });
    setTier(t);
    setResults([]);
    callIndexRef.current = 0;
    setCallIndex(0);
    // The click that picked the tier is the user gesture the browser wants.
    playCall(0);
  };

  // ----- copy -----

  const feedback = () => {
    if (!echo) {
      return "nothing caught that time — hear it again whenever you like, or just start playing.";
    }
    const r = result || compareEcho(planRef.current[callIndex], echo);
    if (r.perfect) {
      return "note for note. That's your ear and your hands agreeing.";
    }
    const extras = r.extra > 0 ? " (a couple of extra notes snuck in.)" : "";
    if (r.matched >= Math.max(1, Math.ceil(r.total * 0.6))) {
      const where =
        r.firstMissDelta == null
          ? `note ${r.firstMissIndex + 1} went missing`
          : `note ${r.firstMissIndex + 1} drifted ${
              r.firstMissDelta > 0 ? "high" : "low"
            }`;
      return `close — ${r.matched} of ${r.total}, and ${where}.${extras}`;
    }
    return `you caught ${r.matched} of ${r.total}. Try humming the call before you play it — humming makes the ear commit.${extras}`;
  };

  // "note for note ×3 · close ×2" — the sit, summed in plain words.
  const endSummary = () => {
    const counts = {};
    results.forEach((g) => {
      counts[g] = (counts[g] || 0) + 1;
    });
    return GRADE_ORDER.filter((g) => counts[g])
      .map((g) => `${GRADE_WORDS[g]} ×${counts[g]}`)
      .join(" · ");
  };

  const endCoda = () => {
    const perfects = results.filter((g) => g === "perfect").length;
    if (perfects === results.length && results.length > 0) {
      return "A clean sweep — your ear led every call home.";
    }
    if (perfects >= Math.ceil(results.length / 2)) {
      return "That's a warm ear. Another sit will make it warmer.";
    }
    return "That's the rep done — the calls get familiar faster than you'd think.";
  };

  const tierLabel = (id) => {
    const t = ECHO_TIERS.find((x) => x.id === id);
    return t ? t.label : id;
  };

  // ----- render -----

  return (
    <div className="hk-moment-body">
      {phase === "pick" && (
        <>
          <p>
            Pick a rung. A sit is five little calls — hear each one, echo it
            back on any keys you like, and we'll say how close you landed.
            Nothing is kept but the warmth.
          </p>
          <div className="hk-choice-row" role="group" aria-label="pick a rung">
            {ECHO_TIERS.map((t) => {
              const offered = !t.requires || Boolean(completed[t.requires]);
              return (
                <button
                  key={t.id}
                  type="button"
                  className="hk-choice"
                  aria-pressed={savedTier === t.id}
                  disabled={!offered}
                  title={
                    offered ? undefined : "this rung opens with the minor side"
                  }
                  onClick={() => startSit(t)}
                >
                  {t.label}
                  {!offered && " · soon"}
                </button>
              );
            })}
          </div>
          {savedTier && (
            <p className="hk-echo-hint">
              last time you sat with “{tierLabel(savedTier)}”.
            </p>
          )}
        </>
      )}

      {inputsLive && (
        <p className="hk-echo-status">
          call {Math.min(callIndex + 1, planRef.current.length)} of{" "}
          {planRef.current.length}
          {tier && ` · ${tier.label}`}
        </p>
      )}

      {phase === "call" && (
        <p className="hk-echo-status">listening… (jump in whenever you like)</p>
      )}

      {phase === "capturing" && (
        <>
          <p className="hk-echo-status" aria-live="polite">
            your turn — echo it back. take your time.
            {noteCount > 0 &&
              ` caught ${noteCount} note${noteCount === 1 ? "" : "s"}…`}
          </p>
          <div className="hk-choice-row">
            <button type="button" className="hk-btn" onClick={finishCapture}>
              done
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={() => playCall()}
            >
              hear it again first
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={advance}
            >
              let this one drift by
            </button>
          </div>
        </>
      )}

      {phase === "review" && (
        <>
          <p className="hk-echo-status">{feedback()}</p>
          <p className="hk-echo-hint">
            the next call rolls in on its own — or just start playing for
            another swing at this one.
          </p>
          <div className="hk-choice-row">
            <button type="button" className="hk-demo-btn" onClick={replayCall}>
              ▶ the call
            </button>
            {echo && (
              <button type="button" className="hk-demo-btn" onClick={replayEcho}>
                ▶ your echo
              </button>
            )}
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={() => playCall()}
            >
              again
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={advance}
            >
              next
            </button>
          </div>
        </>
      )}

      {phase === "end" && (
        <>
          <p className="hk-echo-status">
            the sit, in plain words: {endSummary() || "five calls visited"}.
          </p>
          <p className="hk-echo-hint">{endCoda()}</p>
          <div className="hk-choice-row">
            <button
              type="button"
              className="hk-btn hk-btn--primary"
              onClick={() => tier && startSit(tier)}
            >
              another sit
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={() => setPhaseNow("pick")}
            >
              switch rungs
            </button>
            <button
              type="button"
              className="hk-btn hk-btn--ghost"
              onClick={onExit}
            >
              back to the hall
            </button>
          </div>
        </>
      )}

      {phase !== "pick" && (
        <Piano
          activeMidis={activeMidis}
          onPress={press}
          onRelease={release}
          disabled={phase === "end"}
        />
      )}
    </div>
  );
}
